import { useState, useRef, useEffect, useLayoutEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import {
  FiChevronDown,
  FiBriefcase,
  FiBookOpen,
  FiUsers,
  FiCpu,
  FiCalendar,
  FiMail,
  FiPackage,
  FiHome,
} from "react-icons/fi";
import {
  BUSINESS_TYPE_GROUPS,
  encodeCategory,
  parseCategory,
  getBusinessTypeLabel,
  getBusinessNicheLabel,
  getNichesForType,
} from "../constants/businessCategories";
import Button from "./ds/Button";
import Icon from "./ds/Icon";
import { STATE_LAYER } from "./ds/IconButton";
import SearchField from "./ds/SearchField";

const TYPE_ICONS = {
  "coaching-courses": FiBookOpen,
  "agency-services": FiBriefcase,
  "paid-groups": FiUsers,
  "software-saas": FiCpu,
  events: FiCalendar,
  newsletters: FiMail,
  "physical-products": FiPackage,
  "brick-and-mortar": FiHome,
};

const MENU_WIDTH = 770;
const PHONE = 600;

/** Figma's dropdown item. From 600px the text is Caption 1, small enough for every name to fit one line. */
const Item = ({ selected, onClick, className = "", children }) => (
  <button
    type="button"
    aria-pressed={selected}
    onClick={onClick}
    title={typeof children === "string" ? children : undefined}
    className={`relative flex w-full min-w-0 items-center gap-space-8 rounded-radius-sm px-space-12 py-space-8 text-left text-subheadline min-[600px]:px-space-8 min-[600px]:text-caption-1 ${
      selected ? "bg-fill-quaternary text-accent-text" : "text-label-primary"
    } ${STATE_LAYER} ${className}`}
  >
    <span className="min-w-0 flex-1 truncate">{children}</span>
    {selected && <Icon name="check" size={18} className="shrink-0" />}
  </button>
);

const Label = ({ children }) => <p className="text-caps-label uppercase text-label-tertiary">{children}</p>;

export default function DiscoverCategoryDropdown({ value = "all", onChange, isDark, t }) {
  const [open, setOpen] = useState(false);
  const [draftType, setDraftType] = useState("");
  const [nicheSearch, setNicheSearch] = useState("");
  const [menuPos, setMenuPos] = useState(null);
  const rootRef = useRef(null);
  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const searchRef = useRef(null);

  const parsed = parseCategory(value === "all" ? "" : value);
  const selectedType = parsed.typeId;
  const selectedNiche = parsed.nicheId;
  const hasFilter = value !== "all";

  const TriggerIcon = selectedType ? TYPE_ICONS[selectedType] || FiBriefcase : FiBriefcase;

  useLayoutEffect(() => {
    if (!open || !btnRef.current) {
      setMenuPos(null);
      return undefined;
    }

    const place = () => {
      const r = btnRef.current?.getBoundingClientRect();
      if (!r) return;
      const menuW = Math.min(MENU_WIDTH, window.innerWidth - 16);
      const top = r.bottom + 8;
      setMenuPos({
        top,
        left: Math.max(8, Math.min(r.right - menuW, window.innerWidth - menuW - 8)),
        width: menuW,
        maxHeight: Math.min(710, window.innerHeight - top - 16),
      });
    };

    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    setDraftType(selectedType || BUSINESS_TYPE_GROUPS[0]?.id || "");
    setNicheSearch("");
    const onDoc = (e) => {
      const target = e.target;
      if (rootRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("touchstart", onDoc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("touchstart", onDoc);
    };
  }, [open, selectedType]);

  const niches = useMemo(() => getNichesForType(draftType), [draftType]);

  const filteredNiches = useMemo(() => {
    const q = nicheSearch.trim().toLowerCase();
    if (!q) return niches;
    return niches.filter((nicheId) =>
      getBusinessNicheLabel(t, nicheId).toLowerCase().includes(q)
    );
  }, [niches, nicheSearch, t]);

  const displayLabel = useMemo(() => {
    if (!value || value === "all") {
      return t("home.filterByCategory") || "Category";
    }
    if (selectedType && selectedNiche) {
      return getBusinessNicheLabel(t, selectedNiche);
    }
    if (selectedType) {
      return getBusinessTypeLabel(t, selectedType);
    }
    return t("home.filterByCategory") || "Category";
  }, [value, selectedType, selectedNiche, t]);

  const apply = (next) => {
    onChange(next);
    setOpen(false);
  };

  const nicheActive = (nicheId) => value === encodeCategory(draftType, nicheId);

  const phone = window.innerWidth < PHONE;

  const search = (
    <SearchField
      value={nicheSearch}
      onChange={(e) => setNicheSearch(e.target.value)}
      placeholder={t("createBusiness.searchCategory")}
      className="shrink-0"
    />
  );

  const nicheItems = (
    <>
      {!nicheSearch.trim() && (
        <Item selected={value === draftType} className="col-span-2" onClick={() => apply(draftType)}>
          {t("home.allInType", { type: getBusinessTypeLabel(t, draftType) })}
        </Item>
      )}
      {filteredNiches.map((nicheId) => (
        <Item key={nicheId} selected={nicheActive(nicheId)} onClick={() => apply(encodeCategory(draftType, nicheId))}>
          {getBusinessNicheLabel(t, nicheId)}
        </Item>
      ))}
      {filteredNiches.length === 0 && (
        <p className="col-span-2 px-space-12 py-space-16 text-center text-footnote text-label-tertiary">
          {t("createBusiness.noCategoryResults")}
        </p>
      )}
    </>
  );

  const triggerClass = `inline-flex h-10 max-w-[11rem] items-center gap-1.5 rounded-radius-md px-[14px] text-subheadline-semibold outline-none transition ${
    hasFilter || open ? "bg-accent-default text-accent-on" : "bg-fill-secondary text-label-primary"
  }`;

  return (
    <div ref={rootRef} className="relative shrink-0">
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={triggerClass}
      >
        <TriggerIcon className="h-3.5 w-3.5 shrink-0" />
        <span className="min-w-0 truncate">{displayLabel}</span>
        <FiChevronDown
          className={`h-3.5 w-3.5 shrink-0 transition duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open &&
        menuPos &&
        createPortal(
          <div data-theme={isDark ? "dark" : "light"} className="font-ds text-label-primary">
            {phone ? (
              <>
                <div className="fixed inset-0 z-[600] bg-overlay-scrim animate-ds-fade-in" onClick={() => setOpen(false)} aria-hidden="true" />
                <div
                  ref={menuRef}
                  className="fixed inset-x-0 bottom-0 z-[600] flex max-h-[85vh] flex-col gap-space-12 overflow-y-auto overscroll-contain rounded-t-radius-xl bg-bg-raised p-space-16 shadow-overlay animate-ds-sheet-in"
                >
                  <div className="flex justify-center" aria-hidden="true">
                    <span className="h-[5px] w-[36px] rounded-full bg-fill-secondary" />
                  </div>
                  <div className="flex items-center justify-between gap-space-8">
                    <h2 className="text-headline">{t("home.filterByCategory")}</h2>
                    <Button size="s" variant="plain" onClick={() => apply("all")}>
                      {t("home.allCategories")}
                    </Button>
                  </div>
                  <Label>{t("createBusiness.businessTypeLabel")}</Label>
                  <div className="flex flex-wrap gap-space-8">
                    {BUSINESS_TYPE_GROUPS.map((group) => (
                      <button
                        key={group.id}
                        type="button"
                        aria-pressed={draftType === group.id}
                        onClick={() => setDraftType(group.id)}
                        className={`relative rounded-full px-space-12 py-space-8 text-footnote-semibold ${
                          draftType === group.id ? "bg-accent-tinted text-accent-text" : "bg-fill-quaternary text-label-primary"
                        } ${STATE_LAYER}`}
                      >
                        {getBusinessTypeLabel(t, group.id)}
                      </button>
                    ))}
                  </div>
                  {search}
                  <Label>{t("createBusiness.categoryLabel")}</Label>
                  <div className="flex flex-col">{nicheItems}</div>
                </div>
              </>
            ) : (
              <div
                ref={menuRef}
                style={menuPos}
                className="fixed z-[600] flex overflow-hidden rounded-radius-lg border border-separator-default bg-bg-raised shadow-overlay animate-ds-pop-in"
              >
                <div className="flex w-[290px] shrink-0 flex-col gap-space-4 overflow-y-auto bg-fill-quaternary p-space-12">
                  <Label>{t("createBusiness.businessTypeLabel")}</Label>
                  {BUSINESS_TYPE_GROUPS.map((group) => (
                    <Item key={group.id} selected={draftType === group.id} onClick={() => setDraftType(group.id)}>
                      {getBusinessTypeLabel(t, group.id)}
                    </Item>
                  ))}
                  {hasFilter && (
                    <Button size="s" variant="plain" className="mt-space-4 self-start" onClick={() => apply("all")}>
                      {t("home.allCategories")}
                    </Button>
                  )}
                </div>
                <div className="flex min-w-0 flex-1 flex-col gap-space-12 p-space-12">
                  {search}
                  <Label>{t("createBusiness.categoryLabel")}</Label>
                  <div className="grid min-h-0 grid-cols-2 content-start gap-space-4 overflow-y-auto">{nicheItems}</div>
                </div>
              </div>
            )}
          </div>,
          document.body
        )}
    </div>
  );
}
